import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import Input from '../../../../components/input';
import Form from '../../../../components/_base/Form';
import Button from '../../../../components/button';
import { initiateAttendeeRegistration, registerAttendee } from '../../../../lib/api/public';
import ErrorBlock from '../../../../components/_base/ErrorBlock';
import {
  AttendeeRegistrationRequest,
  CustomFieldsType,
  PublicSessionMeetingDetails
} from '../../../../lib/api';
import {
  ValuesProps,
  TouchedProps,
  ErrorProps,
  ValidatorType,
  settings,
  useValues,
  Formatter,
  FieldsProps,
} from './field-settings';
import { getFormattedValue } from '../../../../hooks/use-form-field';
import './RegistrationForm.scss';
import SelectBox from '../../../shared/SelectBox';
import SessionChoices from './SessionChoices';
import TextAsHTML from '../../../../components/_base/TextAsHTML';
import API, { PUBLIC } from '../../../../lib/api';
import { getItemFromSessionStorage, removeItemFromSessionStorage, setItemInSessionStorage } from '../../../../lib/helpers/storage';
import { PassportNonce, REG_DATA, RegistrationStatus } from '../../../../lib/constants';
import DefaultSpinner from '../../../shared/DefaultSpinner';
import Textarea from '../../../../components/_base/Textarea';
import { useTemplateProcessor } from '../../../../hooks/use-template-processor';
import SafeHTMLRender from '../../../shared/SafeHTMLRender';
import { HTTP_STATUS } from '../../../../config/api';
import { CancelSubscription } from '../../../../hooks/use-registration-subscription';
import { useErrorAnnouncer } from '../../../../hooks/use-error-announcer';

export type IRegistrationFormProps = {
  registrationSubscription: (response: { registrationId: string, appSyncToken: string }) => Promise<CancelSubscription | void>;
  eventId: number;
  toggleRegistrationForm: () => void;
  showSubmitMessage: (v: boolean) => void;
  setErrorRes: (v: boolean) => void;
  customFields: CustomFieldsType[];
  setEmailText: (v: string) => void;
  registrationHeader: string | undefined;
  registrationInstructions: string | undefined;
  setEventToken: (eventToken: string) => void;
  location: { from?: string, errorMsg?: string; email?: string };
  isProRegistrationView: boolean | undefined;
  autoSignInEnabled: boolean | undefined;
  generalSessionList: PublicSessionMeetingDetails[];
  eventTimeZone: string;
  publicDescription: string;
  handleRedirect: (url: string) => void;
  selectedTriageAnswer: string;
  shouldRenderRegisterButton: boolean;
  showSpinner: boolean;
  registrationApprovalEnabled: boolean;
  showApprovalWaitingMessage: (v: boolean) => void;
  urlParams: Record<string, string | null>;
  setRegistrationStatus: (status: RegistrationStatus | undefined) => void;
};

type InputProps = {
  i: number;
  label: string;
  name: string;
  values: ValuesProps;
  touched: TouchedProps;
  errors: ErrorProps;
  optional?: boolean;
  validators: ValidatorType[];
  formatters: Formatter[];
  handleChange: (
    key: string,
    value: string,
    validators: ValidatorType[]) => void;
  handleBlur: (
    key: string,
    value: string,
    validators: ValidatorType[]) => void;
  withOverflowEllipsis?: boolean,
  email?: string;
  onChangeCallback?: (key: string, value: string) => void;
  type?: string;
};

type SubscribeToEventRegistrationResult = {
  registrationId: string;
  cancelSubscription: CancelSubscription | null;
};

export enum CustomFieldType {
  text = 'Text',
  first_name = 'firstName',
  last_name = 'lastName',
  dropdown = 'DROPDOWN',
  checkbox = 'CHECKBOX',
  textarea = 'TEXTAREA'
}

const InputBox: React.FC<InputProps> = (props: InputProps): JSX.Element => {
  const {
    i, name, label, optional, values,
    validators, touched, errors, withOverflowEllipsis,
    handleBlur, handleChange, formatters, email, onChangeCallback, type = 'input'
  } = props;

  useEffect(() => {
    if (email) handleChange('email', email, validators);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e?: React.ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
    const value = getFormattedValue((e?.target?.value ?? ''), formatters);
    handleChange(name, value, validators);
    if (onChangeCallback) {
      onChangeCallback(name, value);
    }
  };

  const onBlur = (name: string) => {
    handleBlur(name, values[name], validators);
  };

  const checkErrors = touched[name] && (!errors[name].map(({ valid }) => valid).every(Boolean) || (!optional && !values[name]));

  const error = touched[name] && errors[name].find(err => !err.valid);
  const errorId = `${name}-error`;
  const describedById = error ? errorId : undefined;
  return (
    <div className='mt-3 word-break' key={`${i}_${name}`}>
      {type === CustomFieldType.textarea ?
        <Textarea
          id={name}
          name={name}
          label={label}
          onChange={onChange}
          error={checkErrors}
          value={values[name]}
          onBlur={() => onBlur(name)}
          span={optional ? '(optional)' : undefined}
          withOverflowEllipsis={withOverflowEllipsis}
          ariaInvalid={!!checkErrors}
          describedById={describedById}
        />
        :
        <Input.Text
          id={name}
          name={name}
          label={label}
          onChange={onChange}
          error={checkErrors}
          value={values[name]}
          onBlur={() => onBlur(name)}
          span={optional ? '(optional)' : undefined}
          withOverflowEllipsis={withOverflowEllipsis}
          ariaInvalid={!!checkErrors}
          describedById={describedById}
        />
      }
      <div className="registration-error-margin absolute">
        {error && <ErrorBlock id={errorId} errorMessage={error.validationMessage || ''} />}
      </div>
    </div>
  );
};

const RegistrationForm: React.FC<IRegistrationFormProps> = (props: IRegistrationFormProps): JSX.Element => {
  const {
    eventTimeZone,
    generalSessionList,
    publicDescription,
    isProRegistrationView,
    selectedTriageAnswer,
    shouldRenderRegisterButton,
    showSpinner,
    urlParams,
  } = props;
  const [selectedMeetings, setSelectedMeetings] = useState<number[]>([]);
  const fields = settings(props.customFields);
  const triageQ = fields.find(f => f.triaged);
  const selectedAnswerFromOptionsList = triageQ?.optionsList?.find(o => o.toLowerCase() === selectedTriageAnswer.toLowerCase());
  const renderRegistrationFields = useTemplateProcessor();
  const mappedUrlParams: Record<string, string | null> = {};
  if (urlParams) {
    const fieldNames = fields.map(f => f.name);
    const externalFieldIds = fields.map(f => f.externalFieldId);
    const fieldTypes = fields.map(f => f.fieldType);
    const optionsListData = fields.map(f => f.optionsList);

    Object.entries(urlParams).forEach(([key, val]) => {
      const value = Array.isArray(val) ? val[0] : val;
      if (!value) return;
      const rawKey = String(key).trim();
      const rawVal = String(value).trim();
      if (rawVal === '') return;
      let normalizedKey: string;
      let normalizedVal: string;
      try {
        normalizedKey = decodeURIComponent(rawKey);
        normalizedVal = decodeURIComponent(rawVal);
      } catch {
        normalizedKey = rawKey;
        normalizedVal = rawVal;
      }

      let fieldIndex = -1;
      
      fieldIndex = fieldNames.findIndex((fn) =>
        fn === normalizedKey || fn.toLowerCase() === normalizedKey.toLowerCase()
      );
      
      if (fieldIndex === -1) {
        fieldIndex = externalFieldIds.findIndex((externalId) => {
          if (!externalId) return false;
          const normalizedExternalId = externalId.trim().toLowerCase();
          const normalizedKeyLower = normalizedKey.toLowerCase();
          if (normalizedExternalId === normalizedKeyLower) {
            return true;
          }
          return normalizedExternalId.includes(normalizedKeyLower);
        });
      }
      
      if (fieldIndex === -1) {
        return; 
      }
      
      const match = fieldNames[fieldIndex];

      if (fieldIndex < fieldTypes.length && fieldTypes[fieldIndex] === "DROPDOWN") {
        const optionsList = optionsListData[fieldIndex];
        if (optionsList && !optionsList.includes(normalizedVal)) {
          return; 
        }
      }

      mappedUrlParams[match] = normalizedVal;
    });
  }
  const {
    state,
    handleChange,
    handleBlur
  } = useValues(fields, { [triageQ?.name as string]: selectedAnswerFromOptionsList as string }, mappedUrlParams);
  const userRegistrationData = useRef<Record<string, string>>({});

  const { values, touched, errors, isDisabledSubmit } = state;
  const errorAnnouncement = useErrorAnnouncer(errors, touched);

  const subscribeToEventRegistration = async (eventId: string): Promise<SubscribeToEventRegistrationResult | null> => {
    try {
      const registrationParams = await API[PUBLIC].fetchRegistrationSubscriptionParams(eventId, encodeURIComponent(selectedTriageAnswer));
      props.showSubmitMessage(true);
      const registrationSubscriptionResult = await props.registrationSubscription(registrationParams);

      return {
        registrationId: registrationParams.registrationId,
        cancelSubscription: typeof registrationSubscriptionResult === 'function' ? registrationSubscriptionResult : null,
      };
    } catch {
      return null;
    }
  };

  const handleResponseStatus = (status: number | null, cancelSubscription: CancelSubscription | null, registrationStatus?: RegistrationStatus): void => {
    switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      props.showSubmitMessage(false);
      return;
    case HTTP_STATUS.FORBIDDEN:
      finalizeRegistration(RegistrationStatus.DENIED);
      cancelSubscription?.();
      return;
    default:
      if (registrationStatus === RegistrationStatus.PENDING || registrationStatus === RegistrationStatus.DENIED) {
        cancelSubscription?.();
      }
      finalizeRegistration(registrationStatus);
      return;
    }
  };

  const finalizeRegistration = (registrationStatus?: RegistrationStatus): void => {
    props.setRegistrationStatus(registrationStatus);
    props.toggleRegistrationForm();
    props.showSubmitMessage(true);
    props.setEmailText(values['email']);
    removeItemFromSessionStorage(REG_DATA);
  };

  const registrationHandle = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (isDisabledSubmit) return;

    let errorResponseStatus: number | null = null;
    let registrationData: { registrationId: string; cancelSubscription: CancelSubscription | null } | null = null;

    try {
      const eventId = props.eventId.toString();
      const params = {
        ...Object.keys(values).reduce<{ [key: string]: string | null }>((result, key) => {
          result[key] = values[key].trim() || null;
          return result;
        }, {}),
        sessionMeetingIds: selectedMeetings
      };

      registrationData = props.autoSignInEnabled && !props.registrationApprovalEnabled
        ? await subscribeToEventRegistration(eventId)
        : null;

      if (registrationData?.registrationId) Reflect.set(params, 'registrationId', registrationData?.registrationId);

      if (triageQ && triageQ.visible === false && selectedTriageAnswer) {
        Reflect.set(params, triageQ.name, selectedAnswerFromOptionsList);
      }
      Reflect.set(params, 'formName', selectedTriageAnswer);

      const externalAttendeeIdKey = Object.keys(urlParams).find(key => 
        key.trim().toLowerCase() === 'externalattendeeid'
      );
      let externalAttendeeId = '';
      if (externalAttendeeIdKey) {
        const rawValue = urlParams[externalAttendeeIdKey];
        const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
        if (value) {
          const normalized = String(value).trim();
          try {
            externalAttendeeId = decodeURIComponent(normalized);
          } catch {
            externalAttendeeId = normalized;
          }
        }
      }
      
      const response = await registerAttendee(eventId, params, externalAttendeeId);

      const registrationStatus = response.registrationStatus;

      if (response.registrationApprovalEnabled) {
        props.showApprovalWaitingMessage(true);
        await subscribeToAttendeeRegistration(response);
      }
      response.eventToken && props.setEventToken(response.eventToken);

      errorResponseStatus = null;
      handleResponseStatus(errorResponseStatus, registrationData?.cancelSubscription ?? null, registrationStatus);
    } catch (e) {
      errorResponseStatus = e?.status;
      props.setErrorRes(true);
      const statusToUse = errorResponseStatus === HTTP_STATUS.BAD_REQUEST ? undefined : RegistrationStatus.DENIED;
      handleResponseStatus(errorResponseStatus, registrationData?.cancelSubscription ?? null, statusToUse);
    }
  };

  const subscribeToAttendeeRegistration = async (data: AttendeeRegistrationRequest): Promise<void> => {
    try {
      await props.registrationSubscription(data);
      await initiateAttendeeRegistration(data);
    } catch (e) {
      props.setErrorRes(true);
    }
  };

  const className = 'editable-sign-in';
  const classNameRegistrationTitle = `${className}--title`;
  const classNameRegistrationSubmitButton = `${className}--submit-button`;

  const saveFormDataInStore = (key: string, value: string): void => {
    const currentUserRegistrationData = JSON.parse(getItemFromSessionStorage(REG_DATA) as string);
    userRegistrationData.current = {
      ...currentUserRegistrationData,
      [key]: value,
    };
    setItemInSessionStorage(REG_DATA, JSON.stringify(userRegistrationData.current));
  };
  const formattedFields = fields.reduce((accumulator, currentValue: FieldsProps) => {
    const [singleFields, nameGroup] = accumulator as [FieldsProps[], FieldsProps];
    const { order } = currentValue;
    const isNameField =
      currentValue.name === CustomFieldType.first_name ||
      currentValue.name === CustomFieldType.last_name;

    return isNameField ?
      [
        singleFields,
        nameGroup.group?.length ?
          {
            ...nameGroup,
            group: nameGroup.group.concat(currentValue)
          } :
          {
            ...nameGroup,
            group: [currentValue],
            order: order,
          }
      ] :
      [singleFields.concat(currentValue), nameGroup];
  }, [[], {}]);

  useEffect(() => {
    const storedRegistrationData = JSON.parse(getItemFromSessionStorage(REG_DATA) as string);
    userRegistrationData.current = storedRegistrationData ? storedRegistrationData : {};
  }, []);

  useEffect(() => {
    const clearRegistrationData = () => {
      removeItemFromSessionStorage(REG_DATA);
    };

    window.addEventListener('beforeunload', clearRegistrationData);
    return () => {
      window.removeEventListener('beforeunload', clearRegistrationData);
    };
  }, []);


  return (
    <Form onSubmit={registrationHandle} name='RegistrationForm'>
      <div className='sr-only' aria-live='polite' aria-atomic='true' role='status'>
        {errorAnnouncement}
      </div>
      {!isProRegistrationView && <h1 className={`${classNameRegistrationTitle} h1-font-size leading-10 break-words`}>
        {props.registrationHeader || 'Register'}
      </h1>}
      {isProRegistrationView && (
        <div className='registration-form__public-description'>
          <TextAsHTML formattedText={renderRegistrationFields(publicDescription)} />
        </div>
      )}
      {props.location?.errorMsg &&
        <div className='mt-3'>
          <span className='break-words font-bold'>
            {props.location.errorMsg}
          </span>
        </div>}
      <div className={`mt-3 ${isProRegistrationView ? 'registration-form__instructions--pro-view' : ''}`}>
        <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={props.registrationInstructions ? renderRegistrationFields(props.registrationInstructions) : ''} className='break-words' />
      </div>
      {(!showSpinner && selectedTriageAnswer)
        ? <DefaultSpinner visible={!showSpinner} />
        : <>
          {([...formattedFields[0] as FieldsProps[], formattedFields[1] as FieldsProps])
            .filter(f => Object.keys(f).length && f.visible !== false)
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((field, i) => {
              if (field.fieldType === CustomFieldType.dropdown || field.fieldType === CustomFieldType.checkbox) {
                return (
                  <SelectBox
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    key={field.name}
                    field={field}
                    values={values}
                    validators={field.validators ?? []}
                    errors={errors}
                    touched={touched}
                    optional={field.optional}
                    onChangeCallback={(key, value) => {
                      saveFormDataInStore(key, value);
                      if (field.triaged) {
                        props.handleRedirect(value);
                      }
                    }}
                  />
                );
              } else if (field.group) {
                return <div className='flex justify-between input-group' key={i}>
                  {
                    field.group.map((fieldInGroup: FieldsProps, j: number) => {
                      return (
                        <InputBox
                          i={j}
                          key={`group-${j}`}
                          name={fieldInGroup?.name}
                          label={fieldInGroup?.label}
                          touched={touched}
                          errors={errors}
                          values={values}
                          handleBlur={handleBlur}
                          optional={fieldInGroup?.optional}
                          handleChange={handleChange}
                          validators={fieldInGroup?.validators ?? []}
                          formatters={fieldInGroup?.formatters ?? []}
                          onChangeCallback={saveFormDataInStore}
                        />
                      );
                    })
                  }
                </div>;
              } else {
                return (
                  <InputBox
                    i={i}
                    key={i}
                    values={values}
                    errors={errors}
                    name={field.name}
                    touched={touched}
                    label={field.label}
                    handleBlur={handleBlur}
                    optional={field.optional}
                    handleChange={handleChange}
                    validators={field.validators ?? []}
                    formatters={field.formatters ?? []}
                    email={props.location?.email}
                    onChangeCallback={saveFormDataInStore}
                    type={field.fieldType}
                  />
                );
              }
            }
            )}
          {!!generalSessionList?.length &&
            (
              <SessionChoices
                data={generalSessionList}
                timeZone={eventTimeZone}
                onChange={(meetingIds: number[]) => setSelectedMeetings(meetingIds)}
                selectedMeetings={selectedMeetings}
              />
            )
          }
          {shouldRenderRegisterButton &&
            <div className={`${classNameRegistrationSubmitButton} flex justify-end mt-6`}>
              <Button.Submit
                name='registration'
                label='Register'
                size={'big'}
                disabled={isDisabledSubmit}
                className='min-w-full px-5 py-3 rounded bg-primary text-white on-hover-shadow'
              />
            </div>
          }
        </>}
    </Form>

  );
};

export default RegistrationForm;
